
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { User } from "firebase/auth";
import type { Timestamp } from "firebase/firestore";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number | null | undefined) {
  if (amount === null || amount === undefined) {
    return "$0.00";
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatCurrencyNoCents(amount: number | null | undefined) {
  if (amount === null || amount === undefined) {
    return "$0";
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(dateString: string | null | undefined) {
  if (!dateString) {
    return 'N/A';
  }
  const date = new Date(dateString);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const correctedDate = new Date(date.getTime() + userTimezoneOffset);

  return correctedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString: string | null | undefined) {
  if (!dateString) {
    return 'N/A';
  }
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDateForTable(dateString: string | null | undefined) {
    if (!dateString) {
      return { datePart: 'N/A', yearPart: '' };
    }
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const yearPart = date.toLocaleDateString('en-US', {
      year: 'numeric',
    });
    return { datePart, yearPart };
}

/**
 * Returns the Firestore collection path for a given user and collection name.
 * e.g., getCollectionPath("user123", "games") => "users/user123/games"
 * @param user The user object from Firebase Auth.
 * @param collectionName The name of the subcollection.
 * @returns The full path to the user-specific subcollection.
 */
export function getCollectionPath(user: { uid: string } | null, collectionName: string): string {
    if (!user || !user.uid) {
        throw new Error("User ID is required to create collection path.");
    }
    return `users/${user.uid}/${collectionName}`;
}

export function toTitleCase(str: string): string {
  if (!str) {
    return "";
  }
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
}

export function formatDuration(minutes: number | undefined | null): string {
    if (minutes === undefined || minutes === null || isNaN(minutes) || minutes < 0) {
        return "Not set";
    }
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h > 0 && m > 0) {
        return `${h}h ${m}m`;
    }
    if (h > 0) {
        return `${h}h`;
    }
    return `${m}m`;
}

export function parseDuration(input: string): number | null {
  if (!input) {
    return null;
  }

  const cleanedInput = input.toLowerCase().trim();

  // Handle formats like "1.5h", "1.5 hours"
  const hourMatch = cleanedInput.match(/([\d.]+)\s*(h|hour|hours)/);
  if (hourMatch) {
    const hours = parseFloat(hourMatch[1]);
    if (!isNaN(hours)) {
      return Math.round(hours * 60);
    }
  }

  // Handle formats like "90m", "90 minutes"
  const minuteMatch = cleanedInput.match(/([\d.]+)\s*(m|min|mins|minutes)/);
  if (minuteMatch) {
    const minutes = parseFloat(minuteMatch[1]);
    if (!isNaN(minutes)) {
      return Math.round(minutes);
    }
  }
  
  // Handle formats like "1h30m"
  const combinedMatch = cleanedInput.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
  if (combinedMatch && (combinedMatch[1] || combinedMatch[2])) {
      const hours = parseInt(combinedMatch[1] || '0', 10);
      const minutes = parseInt(combinedMatch[2] || '0', 10);
      if (!isNaN(hours) || !isNaN(minutes)) {
        return hours * 60 + minutes;
      }
  }
  
  // Handle plain numbers (e.g., "1.5" or "90")
  const numericValue = parseFloat(cleanedInput);
  if (!isNaN(numericValue) && cleanedInput.match(/^[\d.]+$/)) {
    if (cleanedInput.includes('.')) {
      // Assume hours if it's a decimal
      return Math.round(numericValue * 60);
    } else {
      // Assume minutes if it's a whole number
      return Math.round(numericValue);
    }
  }

  return null;
}

/**
 * Format a phone number string to (###) ###-#### format.
 * Returns the original string trimmed if it doesn't contain at least 10 digits.
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length >= 10) {
    const ten = digits.slice(-10);
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6, 10)}`;
  }
  return phone.trim();
}

/**
 * Converts a Firestore Timestamp or string to a Date object.
 * @param value - A Timestamp, string (ISO), or Date
 * @returns A Date object
 */
export function toDate(value: string | Timestamp | Date | null | undefined): Date {
  if (!value) {
    return new Date();
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  // It's a Timestamp
  if (typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date();
}

/**
 * Returns the appropriate default year for financial/tax views.
 * Jan–Apr: previous year (most recently closed tax year).
 * May–Dec: current year.
 */
export function getDefaultYear(now: Date = new Date()): number {
  const month = now.getMonth(); // 0-indexed: 0 = Jan, 4 = May
  const year = now.getFullYear();
  return month < 4 ? year - 1 : year;
}
